import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createBlock as _createBlock, toDisplayString as _toDisplayString, withCtx as _withCtx, createVNode as _createVNode, resolveComponent as _resolveComponent } from "vue"

const _hoisted_1 = { class: "mb-8" }
const _hoisted_2 = { class: "py-5 px-6 rounded bg-racq-white-background flex flex-wrap" }
const _hoisted_3 = { class: "flex" }
const _hoisted_4 = { class: "mr-4" }
const _hoisted_5 = ["src"]
const _hoisted_6 = { class: "text-base mb-4 md:mb-0" }
const _hoisted_7 = ["data-testid", "innerHTML"]
const _hoisted_8 = ["data-testid", "innerHTML"]
const _hoisted_9 = { class: "mt-2 mb-5" }
const _hoisted_10 = { class: "sticky-modal-header" }
const _hoisted_11 = ["innerHTML"]

import { ref, onMounted, watch } from 'vue';
import FormStepFooter from '@/components/FormStepFooter/FormStepFooter.vue';
import { IFormStep, IQuote } from '@/interfaces';
import { useFormStore, useJeopardyStore } from '@/store';
import { replaceLoyaltyReviewBookmarks } from '@/forms/insurance/quote/home-and-contents-purchase/mappings/loyalty-review-mapping';
import useFormInputData from '@/composables/useFormInputData';
import { displayLoyaltyReview } from '@/utils/display-rules/loyalty-rules';
import { RichText } from '@sitecore-jss/sitecore-jss-vue';
import ModalDialog from '../ModalDialog/ModalDialog.vue';


const refreshOriginalQuote = 'refreshOriginalQuote';
const nextClicked = 'nextClicked';

const __default__ = {};

export default /*@__PURE__*/_defineComponent({
  ...__default__,
  __name: 'MembershipLoyaltyReview',
  props: {
    step: {},
    quote: {},
    originalQuote: {},
    storyBookIgnoreApiMode: { type: Boolean },
    checkLoyaltyForSteps: {},
    loyaltyReviewStepId: {},
    hasPremiumChanged: { type: Boolean }
  },
  emits: [refreshOriginalQuote, nextClicked],
  setup(__props: any, { emit: __emit }) {

const formStore = useFormStore();
const jpStore = useJeopardyStore();

/*
This is a little complicated.

1. Loyalty Review relies on the comparing differences between the original quote and the "active" quote.
2. Whenever a party match occurs we don't want to show a stale message that could be inaccurate.
3. So the checkLoyaltyForSteps are the steps you may want to run the loyalty check and it will also update the original quote with the active quote.
4. This means if a loyalty has been shown and the user goes back to one the checkLoyaltyForSteps and no party match is triggered then
    it will not show it again as the original quote is in sync with active quote.
5. If going back to one of the checkLoyaltyForSteps
*/
const props = __props;

const loyaltyLevelResponse = ref<any>();
const heading = ref<string>('');
const loyaltyLevelText = ref<string>('');
const discountText = ref<string>('');
const newQuoteCostText = ref<string>('');
const helpText = ref<string>('');
const quoteChangedModalData = ref<any>();
const showWhyQuoteChangedModal = ref<boolean>(false);
const accountInfoBoxSettings = ref<Record<string, any>>({
  icon: require('@/assets/img/icons/user-icon.svg'),
  unauthenticatedButtonText: '',
  unauthenticatedGreetings: heading,
  params: {
    authenticated: false,
    firstName: '',
  },
});

const emit = __emit;

onMounted(async () => {
  if (!props.quote || !props.quote.quoteType) return;

  quoteChangedModalData.value = props.step.formInputs?.modalWhyHasMyQuoteChanged?.datasource?.fields;

  await checkLoyaltyReview();
  emit(refreshOriginalQuote);
});

watch(
  () => formStore.currentStep,
  async (newValue) => {
    if (props.checkLoyaltyForSteps?.find((x) => x == newValue?.id)) {
      await checkLoyaltyReview();
      emit(refreshOriginalQuote);
    }
  }
);

async function checkLoyaltyReview() {
  loyaltyLevelResponse.value = await displayLoyaltyReview(
    props.quote as IQuote,
    props.originalQuote as IQuote,
    props.storyBookIgnoreApiMode //storyBookIgnoreApiMode is used from storybook, api call fails and will not execute following otherwise.
  );

  if (loyaltyLevelResponse.value.skipLoyaltyReviewStep && props.step.stepIndex) {
    formStore.hideSpecificStep(props.loyaltyReviewStepId);
  }

  updateContent(loyaltyLevelResponse);
}

function updateContent(loyaltyLevelResponse: any) {
  const { formInputData } = useFormInputData(props.step.formInputs!);

  if (!loyaltyLevelResponse.value.skipLoyaltyReviewStep) {
    heading.value = replaceLoyaltyReviewBookmarks(
      formInputData(loyaltyLevelResponse.value.useHeading).label ?? '',
      props.quote
    );

    loyaltyLevelText.value = replaceLoyaltyReviewBookmarks(
      formInputData(loyaltyLevelResponse.value.useLoyaltyText).label ?? '',
      props.quote
    );

    /*
     In sitecore 8 this is only used for Car Hire it seems. litDiscount.Text = string.Format(eligibleForFreeCarHire ? CurrentItem.GetFieldValue("RVMEntitlementWithHireCar_Text") : CurrentItem.GetFieldValue("RVMEntitlementNoHireCar_Text"), discount);
     A content field called textDiscount will need to be added to any step needing this.
    */
    discountText.value = replaceLoyaltyReviewBookmarks(
      formInputData(loyaltyLevelResponse.value.textDiscount).label ?? '',
      props.quote
    );

    newQuoteCostText.value = replaceLoyaltyReviewBookmarks(
      formInputData(loyaltyLevelResponse.value.useNewQuoteCostText).label ?? '',
      props.quote
    );

    helpText.value = replaceLoyaltyReviewBookmarks(
      formInputData(loyaltyLevelResponse.value.useHelpText).label ?? '',
      props.quote
    );
  }
}

async function onSubmit() {
  try {
    formStore.showLoadingForCurrentStep();
    formStore.showLoadingForCurrentStep(false);

    emit(nextClicked);

    formStore.goNextStep();
  } catch (jPCode: any) {
    formStore.showLoadingForCurrentStep(false);
    showJeopardyPanel(jPCode);
  }
}

function showJeopardyPanel(jpCode: string) {
  jpStore.showJeopardyPanel({
    jpCode: jpCode,
  });
}

function onModalClose(e: any) {
  showWhyQuoteChangedModal.value = e;
}

return (_ctx: any,_cache: any) => {
  const _component_FormKit = _resolveComponent("FormKit")!

  return (!loyaltyLevelResponse.value?.skipLoyaltyReviewStep)
    ? (_openBlock(), _createBlock(_component_FormKit, {
        key: 0,
        disabled: !_ctx.step.isActive,
        type: "form",
        actions: false,
        incompleteMessage: false,
        onSubmit: onSubmit
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_1, [
            _createElementVNode("div", _hoisted_2, [
              _createElementVNode("div", _hoisted_3, [
                _createElementVNode("div", _hoisted_4, [
                  _createElementVNode("img", {
                    src: accountInfoBoxSettings.value.icon,
                    alt: "user profile icon",
                    class: "min-w-12 w-12 min-h-12 h-12"
                  }, null, 8, _hoisted_5)
                ]),
                _createElementVNode("div", _hoisted_6, [
                  _createElementVNode("span", {
                    "data-testid": loyaltyLevelResponse.value?.useHeading,
                    class: "text-dark-grey-text pr-1",
                    innerHTML: heading.value
                  }, null, 8, _hoisted_7),
                  (loyaltyLevelResponse.value)
                    ? (_openBlock(), _createElementBlock("span", {
                        key: 0,
                        class: "input-spacing text-base contents text-racq-med-grey",
                        "data-testid": loyaltyLevelResponse.value?.useLoyaltyText,
                        innerHTML: loyaltyLevelText.value
                      }, null, 8, _hoisted_8))
                    : _createCommentVNode("", true)
                ])
              ])
            ]),
            (loyaltyLevelResponse.value)
              ? (_openBlock(), _createBlock(_unref(RichText), {
                  key: 0,
                  class: "text-lg pt-[24px] mb-4 text-dark-grey-text",
                  field: { value: newQuoteCostText.value },
                  "data-testid": loyaltyLevelResponse.value?.useNewQuoteCostText
                }, null, 8, ["field", "data-testid"]))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_9, [
              _createElementVNode("button", {
                type: "button",
                class: "link text-m",
                onClick: _cache[0] || (_cache[0] = ($event: any) => (showWhyQuoteChangedModal.value = true))
              }, _toDisplayString(quoteChangedModalData.value?.modalTriggerLinkText?.value), 1)
            ]),
            (loyaltyLevelResponse.value && discountText.value)
              ? (_openBlock(), _createBlock(_unref(RichText), {
                  key: 1,
                  class: "input-spacing text-lg",
                  field: { value: discountText.value },
                  "data-testid": loyaltyLevelResponse.value?.discountText
                }, null, 8, ["field", "data-testid"]))
              : _createCommentVNode("", true),
            (loyaltyLevelResponse.value)
              ? (_openBlock(), _createBlock(_unref(RichText), {
                  key: 2,
                  class: "input-spacing text-sm text-racq-med-grey",
                  field: { value: helpText.value },
                  "data-testid": loyaltyLevelResponse.value?.useHelpText
                }, null, 8, ["field", "data-testid"]))
              : _createCommentVNode("", true),
            _createVNode(ModalDialog, {
              modelValue: showWhyQuoteChangedModal.value,
              showXCloseButton: true,
              closeOnEsc: false,
              size: "tooltip",
              "onUpdate:modelValue": _cache[1] || (_cache[1] = (value) => onModalClose(value))
            }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_10, _toDisplayString(quoteChangedModalData.value?.modalTitle?.value), 1),
                _createElementVNode("div", {
                  class: "tooltip-body richtext",
                  innerHTML: quoteChangedModalData.value?.modalBody?.value
                }, null, 8, _hoisted_11)
              ]),
              _: 1
            }, 8, ["modelValue"])
          ]),
          _createVNode(FormStepFooter, {
            step: _ctx.step,
            btnNextText: _ctx.step.btnNext,
            btnPrevText: _ctx.step.btnPrev,
            hideEditButton: _ctx.step.hideEditButton
          }, null, 8, ["step", "btnNextText", "btnPrevText", "hideEditButton"])
        ]),
        _: 1
      }, 8, ["disabled"]))
    : _createCommentVNode("", true)
}
}

})